import React, {useContext, useEffect, useMemo, useState} from "react";
import {LayoutContext} from "../../layouts/Default/LayoutContext";
import useTranslate from "../../../modules/local/useTranslate";
import {renderIfElse} from "../../../views/Shared";
import EmptyHolder from "../../EmptyHolder";
import MarkerClusterGroup from "react-leaflet-cluster";
import {Circle, MapContainer, Marker, Popup, TileLayer, useMap} from 'react-leaflet';
import L from 'leaflet';
import {SiOpenstreetmap} from "react-icons/si";
import {DelayRender} from "../../../views/Shared/components/DelayRender";
import "leaflet/dist/leaflet.css";
import {Spin} from "antd";


function ZoomLabel({position, text}) {
    const map = useMap();
    const [icon, setIcon] = useState(createIcon(text, 16)); // font mặc định

    useEffect(() => {
        const updateIcon = () => {
            const zoom = map.getZoom();
            let size;

            if (zoom <= 4) {
                size = 0; // quá xa → ẩn label
            } else if (zoom > 4 && zoom <= 8) {
                size = 8; // nhỏ
            } else if (zoom > 8 && zoom <= 12) {
                size = 12; // nhỏ
            } else {
                size = 16; // zoom gần → font lớn
            }

            setIcon(createIcon(text, size));
        };

        map.on('zoomend', updateIcon);
        updateIcon();

        return () => map.off('zoomend', updateIcon);
    }, [map, text]);

    return <Marker position={position} icon={icon}/>;
}

function createIcon(text, fontSize) {
    return L.divIcon({
        html: `
            <div style="
                display: inline-block;
                padding: 1px 4px;
                font-size: ${fontSize}px;
                font-weight: bold;
                color: #000;
                border-radius: 2px;
                text-align: center;
                text-shadow:
                    -1px -1px 0 #fff,
                    1px -1px 0 #fff,
                    -1px 1px 0 #fff,
                    1px 1px 0 #fff;
                /*white-space: nowrap;*/
            ">
                ${fontSize > 0 ? text : ''}
            </div>
        `,
        className: '',
        iconSize: null
    });
}

function FitBounds({coordinates, zoom}) {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        if (coordinates.length === 1) {
            map.setView([coordinates[0].latitude, coordinates[0].longitude], zoom);
        } else if (coordinates.length > 1) {
            const bounds = L.latLngBounds(
                coordinates.map(loc => [loc.latitude, loc.longitude])
            );
            map.fitBounds(bounds, {padding: [50, 50]});
        }
    }, [map, coordinates, zoom]);

    return null;
}


export const LeafletMap = ({fullScreenAllow = false, location_name = "location", latitude, longitude, coordinates = [], zoomControl = true, zoom = 16, style}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const [isFullScreen, setFullScreen] = useState(false)
    const mapUrl = `https://www.google.com/maps?q=${latitude},${longitude}`
    const [loading, setLoading] = useState(true);

    const Wrapper = useMemo(() => {
        return ({children}) => {
            if (fullScreenAllow === true && isFullScreen) {
                return (
                    <div className="w-full custom-toggle-map relative">
                        <div className="full-screen-mode relative">
                            <div className="p-3 flex flex-1 background-100 rounded-t-lg">
                                <span className="flex items-center flex-1 font-medium text-base text-color-000 tracking-wider">
                                    {t('view map')}
                                </span>
                                <div
                                    style={{
                                        top: !isLg ? 20 : 15,
                                        right: !isLg ? 20 : 15,
                                        fontSize: isLg ? 14 : 16,
                                    }}
                                    className="flex items-center tracking-wider gap-6">
                                    <a
                                        href={mapUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{padding: '4px 10px', borderRadius: '0.375rem'}}
                                        className="background-200 hover:bg-primary-100 font-medium text-sm text-color-100 no-underline">
                                        {t('Get directions')}
                                    </a>
                                    <div
                                        onClick={() => {
                                            setFullScreen(false)
                                        }}
                                        style={{padding: '4px 10px', borderRadius: '0.375rem'}}
                                        className="font-medium text-sm text-color-100 no-underline">
                                        {t('close')}
                                    </div>
                                </div>
                            </div>
                            {loading ? (
                                <div
                                    style={{width: '100%', height: 'calc(100vh - 20%)'}}
                                    className="flex flex-center flex-col gap-2 background rounded-b-lg">
                                    <Spin spinning={true}/>
                                    <span className="text-color-200 italic tracking-wider tracking-wider">
                                        {`${t('loading map')}...`}
                                    </span>
                                </div>
                            ) : (
                                children
                            )}
                        </div>
                    </div>
                );
            }
            return (
                <div
                    style={{ zIndex: 9 }}
                    className="w-full h-full custom-toggle-map relative">
                    <div
                        onClick={() => {
                            setFullScreen(true)
                        }}
                        className="absolute top-0 left-0 mask-map h-full flex flex-col flex-center gap-2 rounded-lg cursor-pointer">
                        <SiOpenstreetmap size={40} className="text-white opacity-85"/>
                        <div className="flex flex-col items-center">
                            <div style={{color: '#fff'}} className="font-semibold leading-snug">
                                {t('view map')}
                            </div>
                            <div className="text-2xs text-white italic opacity-50 leading-tight">
                                {t('click to open the map')}
                            </div>
                        </div>
                    </div>
                    {children}
                </div>
            );
        };
    }, [isFullScreen, isLg, t]);

    return !!latitude && !!longitude ? (
        <Wrapper>
            <DelayRender
                lazy={true}
                time={300}
                LoadingComponent={() => (
                    <div
                        style={{
                            minHeight: !isLg ? 140 : 180,
                            height: !isLg ? 140 : 180,
                            borderRadius: isFullScreen ? '0 0 0.5rem 0.5rem' : '0.5rem',
                            ...style,
                        }}
                        className="w-full flex flex-center background-100">
                        <Spin spinning={true}/>
                    </div>
                )}>
                <MapContainer
                    center={[latitude, longitude]}
                    zoom={zoom}
                    zoomControl={isFullScreen ? true : zoomControl}
                    style={{
                        minHeight: !isLg ? 140 : 180,
                        borderRadius: isFullScreen ? '0 0 0.5rem 0.5rem' : '0.5rem',
                        height: !isLg ? 140 : 180,
                        ...style,
                    }}>
                    {/*<InvalidateSize deps={isFullScreen}/>*/}
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        eventHandlers={{
                            loading: () => {
                                setLoading(true);
                            },
                            load: () => {
                                setLoading(false);
                            },
                        }}
                    />

                    {/* Vùng che Hoàng Sa */}
                    <Circle
                        center={[16.5, 112.0]}   // tâm quần đảo Hoàng Sa
                        radius={250000}           // bán kính 250 km (~ che hết cụm đảo)
                        pathOptions={{
                            color: "transparent",
                            fillColor: "#a9d4e1",
                            fillOpacity: 1,
                        }}
                    />
                    <Circle
                        center={[16.7, 114]}   // tâm quần đảo Hoàng Sa
                        radius={150000}           // bán kính 250 km (~ che hết cụm đảo)
                        pathOptions={{
                            color: "transparent",
                            fillColor: "#a9d4e1",
                            fillOpacity: 1,
                        }}
                    />
                    <ZoomLabel position={[16.5, 112.0]} text="Quần đảo Hoàng Sa"/>
                    {/*<Marker position={[16.5, 112.0]} icon={labelIcon("Quần đảo Hoàng Sa")} />*/}

                    {/* Vùng che Trường Sa */}
                    <Circle
                        center={[10.0, 113.8]}   // tâm quần đảo Trường Sa
                        radius={300000}           // bán kính 400 km (to hơn Hoàng Sa)
                        pathOptions={{
                            color: "transparent",
                            fillColor: "#a9d4e1",
                            fillOpacity: 1,
                        }}
                    />
                    <Circle
                        center={[7, 113.1]}   // tâm quần đảo Trường Sa
                        radius={100000}           // bán kính 400 km (to hơn Hoàng Sa)
                        pathOptions={{
                            color: "transparent",
                            fillColor: "#a9d4e1",
                            fillOpacity: 1,
                        }}
                    />
                    <ZoomLabel position={[10.5, 112.5]} text="Quần đảo Trường Sa"/>
                    {/*<Marker position={[10.0, 114.0]} icon={labelIcon("Quần đảo Trường Sa")} />*/}

                    <MarkerClusterGroup chunkedLoading maxClusterRadius={60} showCoverageOnHover={false}>
                        {renderIfElse(coordinates.length > 0,
                            coordinates.map((location, index) => {
                                return !!location.latitude && (
                                    <Marker key={index} position={[location.latitude, location.longitude]}>
                                        <Popup>
                                            <div className="whitespace-pre-line 111-111" style={{minWidth: 210}}>
                                                {location.name}
                                            </div>
                                        </Popup>
                                    </Marker>
                                )
                            }),
                            <Marker position={[latitude, longitude]}>
                                <Popup>
                                    <div className="italic whitespace-pre-line 222-222" style={{minWidth: 210}}>
                                        {location_name}
                                    </div>
                                </Popup>
                            </Marker>
                        )}
                    </MarkerClusterGroup>
                    <FitBounds coordinates={coordinates} zoom={zoom}/>
                </MapContainer>
            </DelayRender>
        </Wrapper>
    ) : (
        <EmptyHolder/>
    );
};